/**
 * File 객체를 Base64 문자열로 변환
 * @param file 변환할 File 객체
 * @param withMime MIME 타입 포함 여부 (true: data:mime;base64,... 형식, false: base64 문자열만)
 * @returns Base64 문자열
 */
export const fileToBase64 = async (file: File, withMime: boolean = false): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = (): void => {
      const base64String = reader.result as string
      const base64 = base64String.split(',')[1]
      resolve(withMime ? base64String : base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * Base64 문자열을 File 객체로 변환
 * @param base64 Base64 인코딩된 문자열
 * @param filename 생성할 파일명
 * @param mime MIME 타입 (선택사항)
 * @returns File 객체
 */
export const base64ToFile = (base64: string, filename: string, mime?: string): Promise<File> => {
  // data:...;base64, 프리픽스 제거
  const base64Data = base64.includes(',') ? base64.split(',')[1] : base64
  const detectedMime = mime || base64.match(/^data:([^;]+);/)?.[1] || 'application/octet-stream'
  const binaryString = atob(base64Data)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return Promise.resolve(new File([bytes], filename, { type: detectedMime }))
}

/**
 * Blob 객체를 File 객체로 변환
 * @param blob Blob 객체
 * @param filename 생성할 파일명
 * @returns File 객체
 */
export const blobToFile = (blob: Blob, filename: string): File => {
  return new File([blob], filename, { type: blob.type })
}

/**
 * 이미지 File 객체의 너비와 높이를 추출
 * @param file 이미지 File 객체
 * @returns 너비와 높이를 포함한 Promise (로드 실패시 0, 0 반환)
 */
export const getImageSize = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file)
    const img = new Image()

    img.onload = (): void => {
      URL.revokeObjectURL(url)
      resolve({
        width: img.naturalWidth || img.width || 0,
        height: img.naturalHeight || img.height || 0,
      })
    }

    img.onerror = (): void => {
      URL.revokeObjectURL(url)
      resolve({
        width: 0,
        height: 0,
      })
    }

    img.src = url
  })
}
